import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    AlertCircle,
    CheckCircle2,
    Database,
    Loader2,
    Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { getFile, removeMeetingBrainBankExport } from "../../../../service/apiService";
import { BrainBankExport } from "../../../../types";

interface BrainBankExportsCardProps {
    sessionId: string;
    exports: BrainBankExport[];
    /**
     * Name of the meeting's imported PDF, if it has one. Kept for callers;
     * the PDF is now a BrainBank file of its own, so it is named by its own
     * row rather than borrowed onto the transcript's.
     */
    importedFileName?: string | null;
    /** Called after a copy is removed so the parent can refresh the session. */
    onChanged: () => void;
}

/** What a BrainBank file record can be while we watch it. */
type ExportStatus = "unknown" | "not_processed" | "processing" | "processed" | "failed" | "missing";

const TERMINAL: ReadonlySet<ExportStatus> = new Set<ExportStatus>([
    "processed",
    "failed",
    "missing",
]);

// Fast enough to feel live, then slower; a document that is still processing
// after five minutes needs the BrainBank, not this card.
const FAST_INTERVAL_MS = 3000;
const SLOW_INTERVAL_MS = 10000;
const SLOW_AFTER_MS = 30000;
const POLL_CEILING_MS = 5 * 60 * 1000;

const formatDate = (value?: string) => {
    if (!value) return "";
    const date = new Date(value);
    return isNaN(date.getTime()) ? "" : date.toLocaleDateString();
};

const StatusPill: React.FC<{ status: ExportStatus; stalled: boolean }> = ({
    status,
    stalled,
}) => {
    if (status === "processed") {
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <CheckCircle2 className="w-3 h-3" />
                Ready
            </span>
        );
    }

    if (status === "failed") {
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                <AlertCircle className="w-3 h-3" />
                Failed
            </span>
        );
    }

    if (status === "missing") {
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                Removed elsewhere
            </span>
        );
    }

    return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
            {!stalled && <Loader2 className="w-3 h-3 animate-spin" />}
            {stalled ? "Still processing" : "Processing"}
        </span>
    );
};

/**
 * Where this session's transcript has been sent, and what each copy is doing.
 *
 * Status is read from each BrainBank's own file record rather than from
 * anything stored on the session: a cached "Ready" beside a failed ingest is
 * the one thing this card must never show.
 */
const BrainBankExportsCard: React.FC<BrainBankExportsCardProps> = ({
    sessionId,
    exports,
    importedFileName,
    onChanged,
}) => {
    const [statuses, setStatuses] = useState<Record<string, ExportStatus>>({});
    const [stalled, setStalled] = useState(false);
    const [pendingRemoval, setPendingRemoval] = useState<BrainBankExport | null>(null);
    const [removing, setRemoving] = useState(false);

    // Read inside the poll loop so a status update does not restart the timer.
    const statusesRef = useRef(statuses);
    statusesRef.current = statuses;

    const fileIdsKey = useMemo(
        () => exports.map((item) => item.file_id).join("|"),
        [exports]
    );

    useEffect(() => {
        if (!fileIdsKey) return;

        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const startedAt = Date.now();

        const poll = async () => {
            if (cancelled) return;

            const pending = exports.filter(
                (item) => !TERMINAL.has(statusesRef.current[item.file_id] ?? "unknown")
            );
            if (pending.length === 0) return;

            if (Date.now() - startedAt > POLL_CEILING_MS) {
                setStalled(true);
                return;
            }

            // A hidden tab throttles timers anyway; back off rather than queue
            // a burst of requests for when it wakes.
            if (typeof document !== "undefined" && document.hidden) {
                timer = setTimeout(poll, SLOW_INTERVAL_MS);
                return;
            }

            const settled = await Promise.all(
                pending.map(async (item): Promise<[string, ExportStatus]> => {
                    try {
                        const file = await getFile(item.file_id);
                        return [item.file_id, ((file?.status as ExportStatus) || "not_processed")];
                    } catch (err: any) {
                        // Deleted through the BrainBank UI — say so rather than
                        // spinning forever on something that is gone.
                        if (err?.response?.status === 404) return [item.file_id, "missing"];
                        return [item.file_id, statusesRef.current[item.file_id] ?? "unknown"];
                    }
                })
            );

            if (cancelled) return;

            const next = { ...statusesRef.current, ...Object.fromEntries(settled) };
            statusesRef.current = next;
            setStatuses(next);

            const elapsed = Date.now() - startedAt;
            timer = setTimeout(
                poll,
                elapsed > SLOW_AFTER_MS ? SLOW_INTERVAL_MS : FAST_INTERVAL_MS
            );
        };

        poll();

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
        // exports is re-derived from the session on every refresh; the ids are
        // what actually decides whether to restart the loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileIdsKey]);

    const handleRemove = useCallback(async () => {
        if (!pendingRemoval) return;

        setRemoving(true);
        try {
            await removeMeetingBrainBankExport(sessionId, pendingRemoval.file_id);
            toast.success("Removed from the BrainBank. The transcript has been kept.");
            setPendingRemoval(null);
            onChanged();
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === "string" ? detail : "Couldn't remove it. Try again."
            );
        } finally {
            setRemoving(false);
        }
    }, [onChanged, pendingRemoval, sessionId]);

    if (exports.length === 0) return null;

    return (
        <>
            <div className="bg-white border border-gray-200 rounded-xl">
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                        <Database className="w-5 h-5 text-gray-600" />
                        <h3 className="font-medium text-gray-900">BrainBanks</h3>
                    </div>
                    <span className="text-sm text-gray-500">
                        {exports.length} file{exports.length === 1 ? "" : "s"}
                    </span>
                </div>

                <ul className="divide-y divide-gray-100">
                    {exports.map((item) => {
                        const status = statuses[item.file_id] ?? "unknown";
                        return (
                            <li
                                key={item.file_id}
                                className="flex items-center justify-between gap-4 px-4 py-3"
                            >
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                        {item.knowledge_base_name || item.index_name}
                                        <span className="ml-2 text-xs font-normal text-gray-500">
                                            {item.kind === "imported_pdf"
                                                ? "Imported PDF"
                                                : "Transcript"}
                                        </span>
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                        {item.original_file_name || item.file_name}
                                        {item.exported_at && ` · sent ${formatDate(item.exported_at)}`}
                                    </p>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                    <StatusPill status={status} stalled={stalled} />
                                    <button
                                        type="button"
                                        onClick={() => setPendingRemoval(item)}
                                        className="text-sm text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 transition-colors"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>

                {exports.some((item) => statuses[item.file_id] === "failed") && (
                    <p className="px-4 pb-4 text-xs text-gray-500">
                        A failed copy was not indexed. Remove it and send again.
                    </p>
                )}
            </div>

            {pendingRemoval && (
                <div className="fixed inset-0 z-50 flex items-center justify-center scrim">
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="remove-export-title"
                        className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6"
                    >
                        <div className="flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
                                <Trash2 className="w-8 h-8 text-red-500" />
                            </div>
                            <h3
                                id="remove-export-title"
                                className="text-lg font-semibold text-gray-900 mb-2"
                            >
                                Remove from{" "}
                                {pendingRemoval.knowledge_base_name || "this BrainBank"}?
                            </h3>
                            <p className="text-sm text-gray-600 mb-6">
                                The transcript stays in Meetings. Only the BrainBank copy is
                                removed, and it stops appearing in answers from that BrainBank.
                            </p>
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => setPendingRemoval(null)}
                                    disabled={removing}
                                    className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleRemove}
                                    disabled={removing}
                                    className="px-5 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                                >
                                    {removing ? "Removing…" : "Remove"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default BrainBankExportsCard;
